import _each from "lodash/each";
import _pullAt from "lodash/pullAt";
import store from "~/js/store/store";
import { gsap, Quint, Circ, Cubic, Linear } from "gsap";
import Common from "~/js/common/common";
import { EffectFade, Autoplay, Navigation, Pagination, Swiper, Scrollbar, A11y } from "swiper";

export default class Carousel {
  constructor() {
    this.setup();
  }
  setup() {
    const carousels = document.querySelectorAll(".js-carousel");
    this.tweens = [];
    //let carousel = new Swiper('.js-carousel')
    _each(carousels, (elm, index) => {
      let type = elm.getAttribute("data-type");
      if (type === "store-items") {
        this.createStoreItems(elm, index);
      } else {
        this.createSwiper(elm, "jsCarousel_" + index);
      }
    });
  }

  createSwiper(elm, name) {
    const effect = elm.getAttribute("data-effect") || "fade";
    const pagination = elm.querySelector(".js-carousel-pagination") || "";
    let navigation;
    let autoplay = false;
    let modules = [EffectFade, Pagination, A11y];
    let speed = 1200;
    // Parse data-autoplay attribute properly - "1" is enabled, "0" or missing is disabled
    const autoplayAttr = elm.getAttribute("data-autoplay");
    if (autoplayAttr === "1") {
      autoplay = {
        delay: 5200,
        disableOnInteraction: false,
        waitForTransition: false,
        stopOnLastSlide: false
      };
      speed = elm.getAttribute("data-speed") || 1500;
      modules.push(Autoplay);
    }
    if (elm.getAttribute("data-navigation")) {
      navigation = {
        prevEl: elm.querySelector(".js-navigation-prev"),
        nextEl: elm.querySelector(".js-navigation-next")
      };
      modules.push(Navigation);
    }
    const options = {
      effect: effect,
      autoplay: autoplay,
      speed: speed,
      loop: true,
      slidesPerGroup: 1,
      slidesPerView: 1,
      modules: modules,
      fadeEffect: {
        crossFade: true
      },
      a11y: {
        slideLabelMessage: 'slide {{index}} of {{slidesLength}}'
      },
      pagination: {
        el: pagination,
        clickable: true,
        bulletElement: "button",
        type: "custom",
        renderCustom: (swiper, current, total) => {
          let result = "";
          for (let i = 1; i <= total; ++i) {
            let _class = "swiper-pagination-bullet";
            _class += " swiper" + i;
            const isCurrent = i === current;
            let svg = "<svg viewBox='0 0 20 20' fill='none'><circle cx='10' cy='10' r='8' stroke='#333333' stroke-width='4' class='js-circle'/></svg>";
            if (isCurrent) {
              _class += " swiper-pagination-bullet-active";
            }
            result += `<button class='${_class}' ${isCurrent ? 'aria-current="true"' : ''} aria-label="Go to slide ${i}">${svg}</button>`;
          }
          return result;
        }
      },
      navigation,
      on: {
        init: (swiper) => {
          const realSlides = swiper.slides.filter(slide => !slide.classList.contains('swiper-slide-duplicate'));
          realSlides.forEach((slide, index) => {
            slide.setAttribute("aria-label",`Slide ${index + 1} of ${realSlides.length}`)
          });
          
          // Remove aria-controls from navigation buttons if they exist
          if (navigation) {
            this.updateAriaLabel(navigation.prevEl, navigation.nextEl, 'Previous slide', 'Next slide');
            setTimeout(() => {
              this.updateAriaLabel(navigation.prevEl, navigation.nextEl, 'Previous slide', 'Next slide');
            }, 0);
          }

          // Set initial play/pause button state when carousel initializes
          const playPauseButton = elm.querySelector(".js-play-pause");
          if (playPauseButton && autoplay) {
            this.setupPlayPauseButton(false, playPauseButton);
          }
        },
        slideChange: (swiper, progress) => {
          this.updateAccessibility(swiper);
          // Ensure aria-controls is removed even if Swiper re-adds it
          if (navigation) {
            this.updateAriaLabel(navigation.prevEl, navigation.nextEl, 'Previous slide', 'Next slide');
          }
          if (autoplay) {
            //5.2
            this.updateProgress(5.2, progress, elm, name, swiper);
          }
        },
        autoplayStart: (swiper) => {
          const playPauseButton = elm.querySelector(".js-play-pause");
          if (playPauseButton) {
            this.setupPlayPauseButton(false, playPauseButton);
          }
        },
        autoplayStop: (swiper) => {
          const playPauseButton = elm.querySelector(".js-play-pause");
          if (playPauseButton) {
            this.setupPlayPauseButton(true, playPauseButton);
          }
        }
      }
    };
    let carousel = new Swiper(elm.querySelector(".swiper"), options);
    this.updateAccessibility(carousel); // Initial accessibility update
    
    // Set up MutationObserver for navigation buttons if they exist
    if (navigation) {
      if (navigation.prevEl) {
        this.observeAriaControls(navigation.prevEl);
      }
      if (navigation.nextEl) {
        this.observeAriaControls(navigation.nextEl);
      }
      // Also call updateAriaLabel after Swiper is fully initialized
      requestAnimationFrame(() => {
        this.updateAriaLabel(navigation.prevEl, navigation.nextEl, 'Previous slide', 'Next slide');
      });
    }

    // Add Play/Pause Button Control
    const playPauseButton = elm.querySelector(".js-play-pause");
    if (playPauseButton && autoplay) {
      playPauseButton.addEventListener("click", () => {
        // Safely check if autoplay module exists before attempting to control it
        if (!carousel.autoplay) return;
        
        if (carousel.autoplay.running) {
          carousel.autoplay.stop();
          this.setupPlayPauseButton(true, playPauseButton);
        } else {
          carousel.autoplay.start();
          this.setupPlayPauseButton(false, playPauseButton);
        }
      });
    }
  }

  setupPlayPauseButton(isStopped, button) {
    button?.classList.toggle('is-pause', isStopped);
    button?.setAttribute('aria-label', isStopped ? 'Play Slide Autoplay' : 'Pause Slide Autoplay');
  }

  /**
   * Update aria-label and remove aria-controls from navigation buttons
   * @param {HTMLElement} prev - Previous button element
   * @param {HTMLElement} next - Next button element
   * @param {string} prevLabel - Optional aria-label for previous button
   * @param {string} nextLabel - Optional aria-label for next button
   */
  updateAriaLabel(prev, next, prevLabel = 'Previous carousel items', nextLabel = 'Next carousel items') {
    if (prev) {
      prev.removeAttribute('aria-controls');
      if (!prev.getAttribute('aria-label')) {
        prev.setAttribute('aria-label', prevLabel);
      }
    }
    if (next) {
      next.removeAttribute('aria-controls');
      if (!next.getAttribute('aria-label')) {
        next.setAttribute('aria-label', nextLabel);
      }
    }
  }

  /**
   * Use MutationObserver to remove aria-controls whenever Swiper adds it
   * @param {HTMLElement} button - Button element to observe
   * @returns {MutationObserver} - The observer instance
   */
  observeAriaControls(button) {
    if (!button) return null;
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'aria-controls') {
          button.removeAttribute('aria-controls');
        }
      });
    });
    
    observer.observe(button, {
      attributes: true,
      attributeFilter: ['aria-controls']
    });
    
    return observer;
  }

  updateAccessibility(swiper) {
    if (swiper.slides.length) {
      Array.from(swiper.slides).forEach(slide => {
        const isActive = parseInt(slide.getAttribute("data-swiper-slide-index"), 10) === swiper.realIndex && !slide.classList.contains("swiper-slide-duplicate");
        slide.setAttribute("aria-hidden", !isActive);
        slide.querySelectorAll('a, button, input, textarea, select').forEach(el => {
          el.setAttribute('tabindex', isActive ? '0' : '-1');
        });
      });
    }
  }

  updateProgress(autoplayTime, progress, elm, name, swiper) {
    let obj = {
      time: 0
    };
    if (this.tweens) {
      let _index;
      _each(this.tweens, (tw, i) => {
        if (tw.name === name) {
          tw.tween.kill();
          _index = i;
        }
      });
      _pullAt(this.tweens, _index);
    }
    let tween = {
      name: name
    };
    this.resetCircleIndicator(elm);
    tween.tween = gsap.to(obj, {
      time: 50,
      delay: 0,
      duration: autoplayTime,
      ease: Linear.easeNone,
      overwrite: true,
      onUpdate: () => {
        this.updateCircleIndicator(obj.time, swiper.realIndex + 1, elm);
      }
    });
    this.tweens.push(tween);
  }

  resetCircleIndicator(elm) {
    const circles = elm.querySelectorAll(".js-circle");
    _each(circles, (circle) => {
      gsap.set(circle, {
        "stroke-dashoffset": 100
      });
    });
  }

  // 自動再生インジケーターの更新
  updateCircleIndicator(time, currentIndex, elm) {
    const targetCircle = elm.querySelector(".swiper" + currentIndex + " circle");
    //let targetCircle = circles[currentIndex];
    if (targetCircle) {
      gsap.set(targetCircle, {
        "stroke-dashoffset": 100 - time
      });
    }
  }

  /*createLookMvSwiper(elm) {
    const next = elm.getAttribute("data-next") || "";
    const prev = elm.getAttribute("data-prev") || "";
    const pagination = elm.getAttribute("data-pagination") || "";
    const options = {
      effect: "fade",
      autoplay: {
        delay: 4000,
        disableOnInteraction: false,
        stopOnLastSlide: false
      },
      speed: 1200,
      loop: true,
      modules: [Autoplay, EffectFade, Navigation, Pagination],
      fadeEffect: {
        crossFade: true
      },
      navigation: {
        prevEl: document.querySelector(prev),
        nextEl: document.querySelector(next)
      },
      pagination: {
        el: pagination,
        type: "custom",
        renderCustom: (swiper, current, total) => {
          return "<span>" + Common.addZero(current) + "</span> / " + Common.addZero(total);
        }
      }
    };
  }*/

  /**
   * オンラインストア取り扱いアイテム, ピックアップアイテム（ECページ）用
   * @param elm
   */
  createStoreItems(elm, index) {
    let prev = elm.querySelector(".c-carousel-item-prev");
    let next = elm.querySelector(".c-carousel-item-next");
    let scrollbar = elm.querySelector(".swiper-scrollbar");
    let swipers = elm.querySelector(".swiper");
    let slides = elm.querySelectorAll(".swiper-slide");
    if (!Common.isSPSize()) {
      if (slides.length < 4) {
        swipers.classList.add("type-center");
      }
    }

    const updateAriaHidden = (swiper) => {
      let slidesPerView = swiper.params.slidesPerView;
      const activeIndex = swiper.activeIndex;
      swiper.slides.forEach((slide, index) => {
        const isVisible = index >= activeIndex && index < activeIndex + slidesPerView;
        slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
        slide.querySelectorAll('a, button, input, textarea, select').forEach(el => {
          el.setAttribute('tabindex', isVisible ? '0' : '-1');
        });
      });
    };

    const options = {
      speed: 600,
      slidesPerView: 2,
      slidesPerGroup: 1,
      spaceBetween: 10,
      loop: false,
      a11y: {
        slideLabelMessage: 'slide {{index}} of {{slidesLength}}'
      },
      modules: [Autoplay, Navigation, Scrollbar, A11y],
      breakpoints: {
        768: {
          slidesPerView: 4,
          slidesPerGroup: 1,
          //slidesPerGroup: 4,
          spaceBetween: 16
        }
      },
      navigation: {
        prevEl: prev,
        nextEl: next
      },
      scrollbar: {
        el: scrollbar,
        draggable: true
      },
      on: {
        init: (swiper) => {
          updateAriaHidden(swiper);
          this.updateAriaLabel(prev, next);
          // Also call after a short delay to catch aria-controls added after init
          setTimeout(() => {
            this.updateAriaLabel(prev, next);
          }, 0);
        },
        slideChange: (swiper) => {
          updateAriaHidden(swiper);
          // Ensure aria-controls is removed even if Swiper re-adds it
          this.updateAriaLabel(prev, next);
        },
        resize: (swiper) => {
          updateAriaHidden(swiper);
        }
      }
    };
    let carousel = new Swiper(swipers, options);
    
    // Set up MutationObserver to continuously monitor and remove aria-controls
    this.observeAriaControls(prev);
    this.observeAriaControls(next);
    
    // Also call updateAriaLabel after Swiper is fully initialized
    requestAnimationFrame(() => {
      this.updateAriaLabel(prev, next);
    });
  }
}
